import snakecaseKeys from 'snakecase-keys'
import axiosIns from '@/plugins/axios'

export type ConfirmationStatus = 'draft' | 'confirmed'

export type ConfirmationCellType =
  | 'detail'
  | 'skeleton'
  | 'withholding'
  | 'intermediate'

export interface ConfirmationCellAttributes {
  id: number
  key: string
  label: string
  cellType: ConfirmationCellType
  source: string
  value: number
  position: number
  manuallyOverridden: boolean
  businessRuleId: number | null
}

export interface ConfirmationCellResource {
  id: string
  type: string
  attributes: ConfirmationCellAttributes
}

export interface ConfirmationAttributes {
  id: number
  userId: number
  role: string
  status: ConfirmationStatus
  periodStart: string
  periodEnd: string
  recalculatedAt: string | null
  confirmedAt: string | null
  userName: string
  cells: {
    data: ConfirmationCellResource[]
  }
}

export interface ConfirmationResource {
  id: string
  type: string
  attributes: ConfirmationAttributes
}

export interface ConfirmationListResponse {
  status: string
  message: string
  data: {
    data: ConfirmationResource[]
  }
}

export interface ConfirmationSingleResponse {
  status: string
  message: string
  data: {
    data: ConfirmationResource
  }
}

export interface ConfirmationFilterParams {
  role: string
  periodStart: string
  periodEnd: string
}

export interface DestroyDraftsParams {
  role: string
  periodStart: string
  periodEnd: string
}

export interface DestroyDraftsResponse {
  status: string
  message: string
  data: {
    deletedCount: number
  }
}

export type ConfirmationGroupStatus = 'draft' | 'confirmed' | 'mixed'

export interface ConfirmationGroup {
  periodStart: string
  periodEnd: string
  role: string
  total: number
  draftCount: number
  confirmedCount: number
  status: ConfirmationGroupStatus
  lastConfirmedAt: string | null
}

// groups は JSONAPI ではなく plain 配列で、data 直下に ConfirmationGroup[] が入る
export interface ConfirmationGroupListResponse {
  status: string
  message: string
  data: ConfirmationGroup[]
}

export interface IntermediateValue {
  businessRuleId: number
  name: string
  value: number
  aggregationName: string
  displayType: string
}

export interface IntermediateValuesParams {
  role: string
  periodStart: string
  periodEnd: string
  userId: number
}

// intermediate_values は plain 配列で、data 直下に IntermediateValue[] が入る
export interface IntermediateValuesResponse {
  status: string
  message: string
  data: IntermediateValue[]
}

const BASE_PATH = '/v2/employee/payroll/payroll_confirmations'

export default {
  // ロール＋期間で確定データ一覧を取得する
  getConfirmations(params: ConfirmationFilterParams) {
    return axiosIns.get<ConfirmationListResponse>(BASE_PATH, {
      params: snakecaseKeys({ ...params }),
    })
  },

  // 確定データを期間×ロールのグループ単位で一覧取得する
  getConfirmationGroups(params?: { role?: string }) {
    return axiosIns.get<ConfirmationGroupListResponse>(`${BASE_PATH}/groups`, {
      params: snakecaseKeys({ ...(params || {}) }),
    })
  },

  // ロール＋期間で確定データ（draft）を生成する
  buildConfirmations(params: ConfirmationFilterParams) {
    return axiosIns.post<ConfirmationListResponse>(
      `${BASE_PATH}/build`,
      snakecaseKeys({ ...params }),
    )
  },

  // セルを手動更新し、依存セルを再計算した確定データ単体を取得する
  updateCell(id: number, cellKey: string, value: number) {
    return axiosIns.patch<ConfirmationSingleResponse>(
      `${BASE_PATH}/${id}/update_cell`,
      snakecaseKeys({ cellKey, value }),
    )
  },

  // ロール＋期間で確定データを一括最終確定する
  confirmConfirmations(params: ConfirmationFilterParams) {
    return axiosIns.post<ConfirmationListResponse>(
      `${BASE_PATH}/confirm`,
      snakecaseKeys({ ...params }),
    )
  },

  // ロール＋期間で確定データを一括確定解除する
  unconfirmConfirmations(params: ConfirmationFilterParams) {
    return axiosIns.post<ConfirmationListResponse>(
      `${BASE_PATH}/unconfirm`,
      snakecaseKeys({ ...params }),
    )
  },

  // ロール＋期間で draft の確定データのみを削除する（confirmed は保護）
  destroyDrafts(params: DestroyDraftsParams) {
    return axiosIns.delete<DestroyDraftsResponse>(`${BASE_PATH}/destroy_drafts`, {
      params: snakecaseKeys({ ...params }),
    })
  },

  // ユーザー単位で確定データに含めない中間値（集計関連の集計値）を取得する
  getIntermediateValues(params: IntermediateValuesParams) {
    return axiosIns.get<IntermediateValuesResponse>(`${BASE_PATH}/intermediate_values`, {
      params: snakecaseKeys({ ...params }),
    })
  },
}
